import { ApolloClient, InMemoryCache, createHttpLink, gql } from "@apollo/client";
import { QueryClient, useQuery } from "react-query";
import { apiServerUrl } from "src/config";
import { Property, PublicListingNode } from "./usePublicListings";

// The listing itself. Similar properties are a separate, slower query
// (LISTING_RECOMMENDATIONS_QUERY) so the page can show the listing without
// waiting for them.
const LISTING_DETAIL_QUERY = gql`
  query PublicListingDetail($listingId: String!) {
    publicListingDetail(listingId: $listingId) {
      listingId
      price
      deposit
      description
      leaseTerms
      processedPhotoUrls
      showReadyDate
      dateAvailable
      virtualTourUrl
      promoOfferText
      promoOfferStartDate
      promoOfferEndDate
      onboardingRecord {
        edges {
          node {
            tenantStatus
          }
        }
      }
      createdAt
      updatedAt
      isOutForSigning
      hasInstantBook
      property {
        bedrooms
        bathrooms
        sqft
        propertyId
        propertyHash
        fullAddress
        city
        state
        zip
        propertyType
        geoLat
        geoLong
        timezone
        laundry
        heat
        heatingFuel
        airConditioning
        amenitiesNotes
        parkingPrivate
        parkingShared
        parkingCarport
        parkingGuest
        parkingStreet
        waterPayer
        waterReimbursement
        electricityPayer
        electricityReimbursement
        gasPayer
        gasReimbursement
        trashPayer
        trashReimbursement
        internetPayer
        internetReimbursement
        allowedPets
        petPreferences
        refrigerator
        dishwasher
        microwave
        oven
        balcony
        storageUnit
        gym
        pool
        doorman
        alarm
      }
    }
  }
`;

const LISTING_RECOMMENDATIONS_QUERY = gql`
  query PublicListingRecommendations($listingId: String!) {
    publicListingDetail(listingId: $listingId) {
      listingId
      property {
        propertyId
      }
      recommendations {
        listing {
          listingId
          price
          processedPhotoUrls
          isOutForSigning
          property {
            bedrooms
            bathrooms
            sqft
            propertyId
            propertyHash
            fullAddress
            city
            state
            zip
            propertyType
          }
        }
      }
    }
  }
`;

// Extended types for listing detail
export interface MoveInOption {
  moveInDate: string;
  price: number;
}

export interface OnboardingRecordNode {
  tenantStatus?: string | string[];
}

export interface OnboardingRecord {
  edges: {
    node: OnboardingRecordNode;
  }[];
}

export interface DetailedProperty extends Property {
  timezone: string;
  laundry?: string;
  heat?: string;
  heatingFuel?: string;
  airConditioning?: string;
  amenitiesNotes?: string;
  parkingPrivate?: number;
  parkingShared?: number;
  parkingCarport?: boolean;
  parkingGuest?: boolean;
  parkingStreet?: boolean;
  waterPayer?: string;
  waterReimbursement?: boolean;
  electricityPayer?: string;
  electricityReimbursement?: boolean;
  gasPayer?: string;
  gasReimbursement?: boolean;
  trashPayer?: string;
  trashReimbursement?: boolean;
  internetPayer?: string;
  internetReimbursement?: boolean;
  allowedPets?: string[];
  petPreferences?: string;
  refrigerator?: boolean;
  dishwasher?: boolean;
  microwave?: boolean;
  oven?: boolean;
  balcony?: boolean;
  storageUnit?: boolean;
  gym?: boolean | string;
  pool?: boolean | string;
  doorman?: boolean;
  alarm?: boolean;
}

export interface RecommendationListing {
  listing: {
    listingId: string;
    price: number;
    processedPhotoUrls: string[];
    isOutForSigning?: boolean;
    property: {
      bedrooms: number;
      bathrooms: number;
      sqft?: number;
      propertyId: string;
      propertyHash: string;
      fullAddress: string;
      city: string;
      state: string;
      zip: string;
      propertyType: string;
    };
  };
}

export interface DetailedListing extends Omit<PublicListingNode, "property"> {
  leaseTerms?: string;
  dateAvailable?: string;
  virtualTourUrl?: string;
  promoOfferText?: string;
  promoOfferStartDate?: string;
  promoOfferEndDate?: string;
  /** True when lease is signed (out for signing). Not based on lease sent timestamp. */
  isOutForSigning?: boolean;
  property: DetailedProperty;
  moveInOptions?: MoveInOption[];
  onboardingRecord?: OnboardingRecord;
}

export interface ListingDetailResponse {
  publicListingDetail: DetailedListing;
}

export interface ListingRecommendations {
  listingId: string;
  property: { propertyId: string } | null;
  recommendations: RecommendationListing[];
}

interface ListingRecommendationsResponse {
  publicListingDetail: ListingRecommendations | null;
}

// An unauthenticated GraphQL client for public access
const publicGqlClient = new ApolloClient({
  link: createHttpLink({ uri: `${apiServerUrl()}/gql` }),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
    },
  },
});

// Fetch function for single listing detail
async function fetchListingDetail(listingId: string): Promise<ListingDetailResponse> {
  const gqlClient = publicGqlClient;

  try {
    const { data, errors } = await gqlClient.query({
      query: LISTING_DETAIL_QUERY,
      variables: { listingId },
      fetchPolicy: "network-only",
    });

    if (errors) {
      console.error("❌ Listing detail - GraphQL errors:", errors);
      throw new Error(errors[0]?.message || "Failed to fetch listing detail");
    }

    if (!data?.publicListingDetail) {
      console.error("❌ Listing detail - No listing data found");
      throw new Error("Listing not found");
    }

    return data;
  } catch (error) {
    console.error("💥 Listing detail - Query failed:", error);
    throw error;
  }
}

async function fetchListingRecommendations(listingId: string): Promise<ListingRecommendations | null> {
  const { data, errors } = await publicGqlClient.query<ListingRecommendationsResponse>({
    query: LISTING_RECOMMENDATIONS_QUERY,
    variables: { listingId },
  });
  if (errors) {
    throw new Error(errors[0]?.message || "Failed to fetch similar properties");
  }
  return data?.publicListingDetail || null;
}

const cacheOptions = {
  staleTime: 5 * 60 * 1000, // 5 minutes
  cacheTime: 10 * 60 * 1000, // 10 minutes
  retry: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

const listingDetailKey = (listingId: string) => ["listingDetail", listingId];

/**
 * Starts loading a listing before its page's code has arrived, so the two
 * download together (src/index.tsx). useListingDetail then finds it in the
 * cache, or joins the request already in flight.
 */
export function prefetchListingDetail(queryClient: QueryClient, listingId: string) {
  return queryClient.prefetchQuery(listingDetailKey(listingId), () => fetchListingDetail(listingId), cacheOptions);
}

export function useListingDetail(listingId: string) {
  return useQuery<ListingDetailResponse>(listingDetailKey(listingId), () => fetchListingDetail(listingId), {
    enabled: !!listingId,
    ...cacheOptions,
  });
}

export function useListingRecommendations(listingId: string) {
  return useQuery<ListingRecommendations | null>(
    ["listingRecommendations", listingId],
    () => fetchListingRecommendations(listingId),
    { enabled: !!listingId, ...cacheOptions }
  );
}
